const FORBIDDEN_KEYS = new Set([
  'children',
  'className',
  'style',
  'height',
  'width',
  'banner',
  'dialog',
  'render',
  'component',
  'jsx',
  'geojson',
  'mapInstance',
]);

export function failOperationalDescriptor(path: string, message: string): never {
  throw new Error(`Invalid operational workspace descriptor: ${path} ${message}.`);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function operationalObject(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (!isObject(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    return failOperationalDescriptor(path, 'must be a plain object');
  }
  return value;
}

export function exactOperationalKeys(
  value: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  path: string,
): void {
  for (const key of Object.keys(value)) {
    if (FORBIDDEN_KEYS.has(key)) {
      failOperationalDescriptor(`${path}.${key}`, 'is renderer-owned and forbidden');
    }
    if (!allowed.has(key)) {
      failOperationalDescriptor(`${path}.${key}`, 'is not part of the sealed contract');
    }
  }
}

export function operationalText(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return failOperationalDescriptor(path, 'must be non-empty plain text');
  }
  return value;
}

export function operationalOneOf(
  value: unknown,
  allowed: readonly string[],
  path: string,
): string {
  const resolved = operationalText(value, path);
  if (!allowed.includes(resolved)) {
    failOperationalDescriptor(path, `must be one of ${allowed.join(', ')}`);
  }
  return resolved;
}

export function optionalOperationalText(value: unknown, path: string): void {
  if (value !== undefined) operationalText(value, path);
}

export function operationalArray(
  value: unknown,
  path: string,
): readonly unknown[] {
  if (!Array.isArray(value)) {
    return failOperationalDescriptor(path, 'must be an array');
  }
  return value;
}
